import { Component, type ReactNode } from "react";

interface Props { children: ReactNode; fallback?: ReactNode; }
interface State { hasError: boolean; error: Error | null; }

function isChunkLoadError(error: Error | null): boolean {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("loading chunk") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module")
  );
}

function ChunkErrorBanner() {
  return (
    <div className="flex min-h-[400px] items-center justify-center p-8">
      <div className="text-center space-y-4">
        <h2 className="text-xl font-semibold">A new update is available</h2>
        <p className="text-muted-foreground">
          The app has been updated. Please reload to get the latest version.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
        >
          Reload Now
        </button>
      </div>
    </div>
  );
}

export { isChunkLoadError, ChunkErrorBanner };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return <ChunkErrorBanner />;
      }
      return this.props.fallback || (
        <div className="flex min-h-[400px] items-center justify-center p-8">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold text-destructive">Something went wrong</h2>
            <p className="text-muted-foreground">{this.state.error?.message}</p>
            <button onClick={() => this.setState({ hasError: false, error: null })} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Try again</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
